import { useEffect, useRef, useState, useCallback } from 'react'
import { saveScore } from '../utils/firestoreService'
import GameRanking from '../components/GameRanking'
import './Tetris.css'

const BOARD_WIDTH = 10
const BOARD_HEIGHT = 20
const BLOCK_SIZE = 30

const TETROMINOS = {
  I: { shape: [[1,1,1,1]], color: '#00f0f0' },
  O: { shape: [[1,1],[1,1]], color: '#f0f000' },
  T: { shape: [[0,1,0],[1,1,1]], color: '#a000f0' },
  S: { shape: [[0,1,1],[1,1,0]], color: '#00f000' },
  Z: { shape: [[1,1,0],[0,1,1]], color: '#f00000' },
  J: { shape: [[1,0,0],[1,1,1]], color: '#0000f0' },
  L: { shape: [[0,0,1],[1,1,1]], color: '#f0a000' }
}

export default function Tetris({ user, onBackToMenu }) {
  const canvasRef = useRef()
  const [board, setBoard] = useState(createBoard())
  const [currentPiece, setCurrentPiece] = useState(null)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [score, setScore] = useState(0)
  const [gameOver, setGameOver] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [level, setLevel] = useState(1)
  const [lines, setLines] = useState(0)
  const [savingScore, setSavingScore] = useState(false)
  const [scoreSaved, setScoreSaved] = useState(false)
  const gameLoopRef = useRef()

  function createBoard() {
    return Array.from({ length: BOARD_HEIGHT }, () => Array(BOARD_WIDTH).fill(0))
  }

  function getRandomPiece() {
    const pieces = Object.keys(TETROMINOS)
    const randomPiece = pieces[Math.floor(Math.random() * pieces.length)]
    return { ...TETROMINOS[randomPiece], type: randomPiece }
  }

  function collision(piece, pos, brd = board) {
    for (let y = 0; y < piece.shape.length; y++) {
      for (let x = 0; x < piece.shape[y].length; x++) {
        if (piece.shape[y][x]) {
          const newY = pos.y + y
          const newX = pos.x + x
          if (newY < 0 || newY >= BOARD_HEIGHT || newX < 0 || newX >= BOARD_WIDTH || (brd[newY] && brd[newY][newX])) {
            return true
          }
        }
      }
    }
    return false
  }

  function merge(brd, piece, pos) {
    const newBoard = brd.map(row => [...row])
    piece.shape.forEach((row, y) => {
      row.forEach((value, x) => {
        if (value) {
          const boardY = pos.y + y
          const boardX = pos.x + x
          if (boardY >= 0 && boardY < BOARD_HEIGHT) {
            newBoard[boardY][boardX] = piece.color
          }
        }
      })
    })
    return newBoard
  }

  function clearLines(brd) {
    let linesCleared = 0
    const newBoard = brd.filter(row => {
      if (row.every(cell => cell !== 0)) {
        linesCleared++
        return false
      }
      return true
    })
    while (newBoard.length < BOARD_HEIGHT) {
      newBoard.unshift(Array(BOARD_WIDTH).fill(0))
    }
    return { newBoard, linesCleared }
  }

  const rotate = useCallback((piece) => {
    const rotated = piece.shape[0].map((_, i) =>
      piece.shape.map(row => row[i]).reverse()
    )
    return { ...piece, shape: rotated }
  }, [])

  const moveDown = useCallback(() => {
    if (!currentPiece || gameOver || isPaused) return

    const newPos = { ...position, y: position.y + 1 }
    if (!collision(currentPiece, newPos)) {
      setPosition(newPos)
    } else {
      const mergedBoard = merge(board, currentPiece, position)
      const { newBoard, linesCleared } = clearLines(mergedBoard)
      
      let newScore = score
      let newLines = lines
      let newLevel = level
      
      if (linesCleared > 0) {
        const points = [0, 100, 300, 500, 800][linesCleared] * level
        newScore = score + points
        newLines = lines + linesCleared
        newLevel = Math.floor(newLines / 10) + 1
        setScore(newScore)
        setLines(newLines)
        setLevel(newLevel)
      }

      setBoard(newBoard)
      const newPiece = getRandomPiece()
      const newPosition = { x: Math.floor(BOARD_WIDTH / 2) - 1, y: 0 }
      
      if (collision(newPiece, newPosition, newBoard)) {
        setGameOver(true)
        saveScoreToFirebase(newScore, newLevel, newLines)
      } else {
        setCurrentPiece(newPiece)
        setPosition(newPosition)
      }
    }
  }, [currentPiece, position, board, gameOver, isPaused, level, lines, score])

  async function saveScoreToFirebase(finalScore, finalLevel, finalLines) {
    if (!user) {
      console.log('No user logged in')
      return
    }
    
    if (finalScore === 0) {
      console.log('Score is 0, not saving')
      return
    }
    
    try {
      setSavingScore(true)
      console.log('Saving Tetris score:', finalScore)
      
      await saveScore(user, finalScore, finalLevel, finalLines, 'tetris')
      
      setSavingScore(false)
      setScoreSaved(true)
    } catch (error) {
      console.error('Erro ao salvar pontuação:', error)
      setSavingScore(false)
    }
  }

  // Previne scroll durante o jogo
  useEffect(() => {
    document.body.classList.add('game-active')
    
    return () => {
      document.body.classList.remove('game-active')
    }
  }, [])

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (gameOver || isPaused) return

      if (e.key === 'ArrowLeft') {
        const newPos = { ...position, x: position.x - 1 }
        if (currentPiece && !collision(currentPiece, newPos)) {
          setPosition(newPos)
        }
      } else if (e.key === 'ArrowRight') {
        const newPos = { ...position, x: position.x + 1 }
        if (currentPiece && !collision(currentPiece, newPos)) {
          setPosition(newPos)
        }
      } else if (e.key === 'ArrowDown') {
        moveDown()
      } else if (e.key === 'ArrowUp' || e.key === ' ') {
        if (currentPiece) {
          const rotatedPiece = rotate(currentPiece)
          if (!collision(rotatedPiece, position)) {
            setCurrentPiece(rotatedPiece)
          }
        }
      } else if (e.key === 'p' || e.key === 'P') {
        setIsPaused(prev => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [currentPiece, position, gameOver, isPaused, moveDown, rotate])

  useEffect(() => {
    if (!currentPiece) {
      const piece = getRandomPiece()
      setCurrentPiece(piece)
      setPosition({ x: Math.floor(BOARD_WIDTH / 2) - 1, y: 0 })
    }
  }, [currentPiece])

  useEffect(() => {
    if (gameOver || isPaused) return

    const speed = Math.max(100, 800 - (level - 1) * 50)
    gameLoopRef.current = setInterval(moveDown, speed)
    return () => clearInterval(gameLoopRef.current)
  }, [moveDown, gameOver, isPaused, level])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Draw grid
    ctx.strokeStyle = '#1a1a2e22'
    for (let y = 0; y <= BOARD_HEIGHT; y++) {
      ctx.beginPath()
      ctx.moveTo(0, y * BLOCK_SIZE)
      ctx.lineTo(BOARD_WIDTH * BLOCK_SIZE, y * BLOCK_SIZE)
      ctx.stroke()
    }
    for (let x = 0; x <= BOARD_WIDTH; x++) {
      ctx.beginPath()
      ctx.moveTo(x * BLOCK_SIZE, 0)
      ctx.lineTo(x * BLOCK_SIZE, BOARD_HEIGHT * BLOCK_SIZE)
      ctx.stroke()
    }

    // Draw board
    board.forEach((row, y) => {
      row.forEach((cell, x) => {
        if (cell) {
          ctx.fillStyle = cell
          ctx.fillRect(x * BLOCK_SIZE, y * BLOCK_SIZE, BLOCK_SIZE - 2, BLOCK_SIZE - 2)
          ctx.strokeStyle = '#ffffff44'
          ctx.strokeRect(x * BLOCK_SIZE, y * BLOCK_SIZE, BLOCK_SIZE - 2, BLOCK_SIZE - 2)
        }
      })
    })

    // Draw current piece
    if (currentPiece) {
      currentPiece.shape.forEach((row, y) => {
        row.forEach((value, x) => {
          if (value) {
            const drawX = (position.x + x) * BLOCK_SIZE
            const drawY = (position.y + y) * BLOCK_SIZE
            ctx.fillStyle = currentPiece.color
            ctx.fillRect(drawX, drawY, BLOCK_SIZE - 2, BLOCK_SIZE - 2)
            ctx.strokeStyle = '#ffffff88'
            ctx.strokeRect(drawX, drawY, BLOCK_SIZE - 2, BLOCK_SIZE - 2)
          }
        })
      })
    }
  }, [board, currentPiece, position])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    let touchStartX = 0
    let touchStartY = 0
    let touchStartTime = 0

    const handleTouchStart = (e) => {
      if (gameOver || isPaused) return
      const touch = e.touches[0]
      touchStartX = touch.clientX
      touchStartY = touch.clientY
      touchStartTime = Date.now()
    }

    const handleTouchEnd = (e) => {
      if (gameOver || isPaused) return
      const touch = e.changedTouches[0]
      const touchEndX = touch.clientX
      const touchEndY = touch.clientY
      const touchDuration = Date.now() - touchStartTime

      const deltaX = touchEndX - touchStartX
      const deltaY = touchEndY - touchStartY
      const absDeltaX = Math.abs(deltaX)
      const absDeltaY = Math.abs(deltaY)

      const minSwipeDistance = 30

      if (touchDuration < 200 && absDeltaX < minSwipeDistance && absDeltaY < minSwipeDistance) {
        if (currentPiece) {
          const rotatedPiece = rotate(currentPiece)
          if (!collision(rotatedPiece, position)) {
            setCurrentPiece(rotatedPiece)
          }
        }
      } else if (absDeltaX > minSwipeDistance || absDeltaY > minSwipeDistance) {
        if (absDeltaX > absDeltaY) {
          if (deltaX > 0) {
            const newPos = { ...position, x: position.x + 1 }
            if (currentPiece && !collision(currentPiece, newPos)) {
              setPosition(newPos)
            }
          } else {
            const newPos = { ...position, x: position.x - 1 }
            if (currentPiece && !collision(currentPiece, newPos)) {
              setPosition(newPos)
            }
          }
        } else {
          if (deltaY > 0) {
            moveDown()
          }
        }
      }
    }

    canvas.addEventListener('touchstart', handleTouchStart)
    canvas.addEventListener('touchend', handleTouchEnd)

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart)
      canvas.removeEventListener('touchend', handleTouchEnd)
    }
  }, [currentPiece, position, gameOver, isPaused, moveDown, rotate])

  function resetGame() {
    setBoard(createBoard())
    setCurrentPiece(null)
    setPosition({ x: 0, y: 0 })
    setScore(0)
    setGameOver(false)
    setIsPaused(false)
    setLevel(1)
    setLines(0)
    setSavingScore(false)
    setScoreSaved(false)
  }

  return (
    <div className="tetris-container">
      <div className="game-header">
        <div className="user-info">
          {user?.photoURL ? (
            <img src={user.photoURL} alt="User" className="user-photo" />
          ) : (
            <div className="user-photo user-photo-placeholder">
              {(user?.displayName || user?.email || 'U').charAt(0).toUpperCase()}
            </div>
          )}
          <span className="user-name">{user?.displayName || user?.email || 'Usuário'}</span>
        </div>
        <h1 className="game-title">TETRIS</h1>
        <div className="header-spacer"></div>
      </div>

      <div className="game-layout">
        <div className="sidebar left-sidebar">
          <div className="info-panel">
            <h3>PONTUAÇÃO</h3>
            <div className="stat-value">{score}</div>
          </div>
          <div className="info-panel">
            <h3>LINHAS</h3>
            <div className="stat-value">{lines}</div>
          </div>
          <div className="info-panel">
            <h3>NÍVEL</h3>
            <div className="stat-value">{level}</div>
          </div>
        </div>

        <div className="game-area">
          <canvas 
            ref={canvasRef} 
            width={BOARD_WIDTH * BLOCK_SIZE} 
            height={BOARD_HEIGHT * BLOCK_SIZE}
            className="game-canvas"
          />
          
          {/* Info Mobile Overlay */}
          <div className="mobile-info-overlay">
            <span>🏆 {score}</span>
            <span>📊 L{level}</span>
            <span>📏 {lines}</span>
          </div>
          
          {gameOver && (
            <div className="game-overlay">
              <div className="overlay-content">
                <h2>GAME OVER</h2>
                <p className="final-score">Pontuação Final: <strong>{score}</strong></p>
                <p className="final-stats">Nível {level} • {lines} linhas</p>
                
                {savingScore && (
                  <p className="saving-message">💾 Salvando pontuação...</p>
                )}
                
                {scoreSaved && (
                  <p className="saved-message">✅ Pontuação salva com sucesso!</p>
                )}
                
                <div className="game-over-buttons">
                  <button className="btn-primary" onClick={resetGame}>
                    🎮 Jogar Novamente
                  </button>
                  {onBackToMenu && (
                    <button className="btn-secondary" onClick={onBackToMenu}>
                      ← Voltar ao Menu
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          {isPaused && !gameOver && (
            <div className="game-overlay">
              <div className="overlay-content">
                <h2>PAUSADO</h2>
                <p>Pressione P para continuar</p>
              </div>
            </div>
          )}
        </div>

        <div className="sidebar right-sidebar">
          <div className="controls-panel">
            <h3>CONTROLES</h3>
            <div className="control-item">← → Mover</div>
            <div className="control-item">↑ / Space Girar</div>
            <div className="control-item">↓ Descer</div>
            <div className="control-item">P Pausar</div>
            {onBackToMenu && (
              <button className="btn-back-menu" onClick={onBackToMenu}>
                ← Menu
              </button>
            )}
          </div>
          <button className="btn-secondary" onClick={() => setIsPaused(!isPaused)}>
            {isPaused ? 'Continuar' : 'Pausar'}
          </button>
          <button className="btn-secondary" onClick={resetGame}>Reiniciar</button>
        </div>
      </div>

      {/* Ranking em Tempo Real */}
      {/* Ranking em Tempo Real */}
      <div className="ranking-container">
        <GameRanking 
          gameName="tetris"
          currentScore={score}
          user={user}
        />
      </div>
    </div>
  )
}
